import { useEffect, useState } from 'react';
import { recordedAmount } from '../form/amountFields';
import { GroupMember } from '../grouping';
import { ExpenditureCategory } from '../hooks/useExpenditures';
import { conversionPreviewNote } from '../leftOut';
import { expenditureCategoryOptions } from '../redesign/expenditureCategories';
import { CashflowModalShell } from '../redesign/modal/CashflowModalShell';
import { ExpenditureRow } from '../rows';
import { CashflowFrequency, calculateAnnualAmount, cashflowFrequencyOptions } from '../utils';
import { ownersFrom } from './jointAllocations';
import { ownersOf } from './owners';
import { useJointOwnership } from './useJointOwnership';
import { JointDeletion, JointExpenditureSubmission } from './useJointSaves';

type JointExpenditureModalProps = {
  opened: boolean;
  onClose: () => void;
  onSubmit: (submission: JointExpenditureSubmission) => Promise<void>;
  members: GroupMember[];
  defaultClientId?: string;
  /** The client whose own tab the form was opened from; absent on the family tab. */
  viewingClientId?: string;
  initialRow?: ExpenditureRow;
  onDelete: (deletion: JointDeletion) => Promise<void>;
};

/** An expenditure is one amount, held in shares by the people it applies to. */
export function JointExpenditureFormModal({
  opened,
  onClose,
  onSubmit,
  members,
  defaultClientId,
  viewingClientId,
  initialRow,
  onDelete,
}: JointExpenditureModalProps) {
  const [category, setCategory] = useState<ExpenditureCategory | ''>('');
  const [frequency, setFrequency] = useState<CashflowFrequency | ''>('Monthly');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState<number | ''>('');

  const stored = initialRow?.items[0];
  const storedOwners = stored ? ownersOf(stored) : undefined;
  const ownership = useJointOwnership({
    opened,
    noun: 'expenditure',
    storedId: stored?.id,
    storedOwners,
    members,
    defaultClientId,
    viewingClientId,
    onDelete,
  });
  const { allocations } = ownership;

  useEffect(() => {
    if (!opened) return;
    setCategory((initialRow?.category as ExpenditureCategory) ?? '');
    // Detected records can arrive without a frequency, so leave it for the adviser to choose
    setFrequency(initialRow ? (initialRow.frequency ?? '') : 'Monthly');
    setDescription(initialRow?.description ?? '');
    setAmount(initialRow?.amount ?? '');
  }, [opened, initialRow]);

  const chosenFrequency = frequency || null;
  const wholeAmount = recordedAmount(amount);
  const annual = (value: number | null) => calculateAnnualAmount(value, chosenFrequency);
  const summary = [
    {
      label: 'Annual spend',
      value: annual(wholeAmount),
      parts: ownership.shareParts(wholeAmount, annual),
    },
  ];
  const handleSubmit = () => {
    if (ownership.isSaving || !category || !frequency) return;
    return ownership.save(() =>
      onSubmit({
        id: stored?.id,
        category,
        frequency,
        description,
        amount: wholeAmount,
        owners: ownersFrom(allocations, storedOwners),
        storedClientIds: ownership.storedClientIds,
      }),
    );
  };

  return (
    <CashflowModalShell
      opened={opened}
      onClose={onClose}
      title={initialRow ? 'Edit expenditure' : 'Add expenditure'}
      submitLabel={initialRow ? 'Save expenditure' : 'Add expenditure'}
      onSubmit={handleSubmit}
      onFieldChange={ownership.clearRefusal}
      isSaving={ownership.isSaving}
      summaryNote={conversionPreviewNote(chosenFrequency)}
      notice={ownership.notice}
      summary={summary}
      testId="add-expenditure-modal"
      fields={[
        [
          {
            kind: 'select',
            label: 'Category',
            value: category,
            onChange: value => setCategory(value as ExpenditureCategory),
            data: expenditureCategoryOptions,
            placeholder: 'Search categories',
            required: true,
            searchable: true,
          },
          {
            kind: 'select',
            label: 'Frequency',
            value: frequency,
            onChange: value => setFrequency(value as CashflowFrequency),
            data: cashflowFrequencyOptions,
            required: true,
          },
        ],
        [
          { kind: 'text', label: 'Description', value: description, onChange: setDescription },
          { kind: 'money', label: 'Amount', value: amount, onChange: setAmount },
        ],
      ]}
      allocation={{
        ...ownership.allocationProps,
        share: { amountLabel: 'Amount', of: wholeAmount, assumed: ownership.assumedShare },
        extraFields: [],
      }}
      deletion={ownership.deletion}
    />
  );
}
